import { IBasePaginatedFilter } from '@howdeniberia/core-front';

export enum TemporalState {
  Inactive = 0,
  Active = 1,
}

export interface ICalculatorBaseRequest {
  paymentType?: number;
  professionCode?: number;
  clientId?: number;
  effectiveDate?: string | Date;
  birthDate?: string | Date;
  regime?: string;
}

export interface ILifeRateOutputDto {
  feeDeath: number;
  feeIptp: number;
  feeIap: number;
  feeSeriousIllness: number;
}

export interface IResponseMessage {
  html: string;
  blocking?: boolean;
}

export interface IResponseOutputDto {
  chargeable: boolean;
  bonus: number;
  productName: string;
  trace: string;
  maximumCapital: number;
  hasTelesubscription: boolean;
  telesubscriptionDirect: boolean;
  telesubscriptionSingleQuestion: boolean;
  summaryCapitals: string;

  messages: IResponseMessage[];
}

export interface ITRateOutputDto {
  annualRate: number;
  rateFor545Days: number;
  rateFor720Days: number;
  hospitalization: number;
}

export interface IRateDependencyOutputDto {
  capitalRate50K: number;
  capitalRate100K: number;
  capitalRate150K: number;
  capitalRate200K: number;
  capitalRate250K: number;
  capitalRate300K: number;
  incomeRate: number;
}

export interface ICalculatorAAxaRequest extends ICalculatorBaseRequest {
  capitalDeath?: number;
  capitalDeathTrafficAccident?: number;
  capitalHeartAttack?: number;
  capitalDisabilityType?: number;
  capitalDisability?: number;
  capitalPermanentDisabilityTrafficAccident?: number;
  capitalMedicalExpensesForAccidents?: number;
  capitalHospitalizationIllnessAccident?: number;
  capitalFamilyAccidentAssistance?: number;
  capitalHIVHepatitisInfection?: number;
}

export interface ICalculatorASurneRequest extends ICalculatorBaseRequest {
  capitalDeath?: number;
  capitalHeartAttack?: number;
  capitalAbsoluteDisabilityType?: number;
  capitalAbsoluteDisability?: number;
  capitalTotalPermanentDisability?: number;
  capitalHealthDisabilityType?: number;
  rateIdHIVHepatitisInfection?: number;
}

export interface ICalculatorAPrebalRequest extends ICalculatorBaseRequest {
  capitalDeath?: number;
  capitalDeathTrafficAccident?: number;
  capitalDisabilityType?: number;
  capitalDisability?: number;
  hasSelectedCapitalDisability?: number;
  capitalHealthCareAccidents?: number;
  rateIdHIVHepatitisInfection?: number;
}

export interface ICalculatorITPmEliteRequest extends ICalculatorBaseRequest {
  subsidy?: number;
  franchise?: number;
  duration?: number;
  hospitalization?: number;
}

export interface ICalculatorITPmCoegRequest extends ICalculatorBaseRequest {
  subsidy?: number;
  franchise?: number;
  duration?: number;
}

export interface ICalculatorITSurnePlusRequest extends ICalculatorBaseRequest {
  subsidy?: number;
  franchise?: number;
  duration?: number;
  hospitalization?: number;
}

export interface ICalculatorITSurneVipRequest extends ICalculatorBaseRequest {
  subsidy?: number;
  franchise?: number;
  duration?: number;
  hospitalization?: number;
}

export interface ICalculatorITPrebalRequest extends ICalculatorBaseRequest {
  subsidy?: number;
  franchise?: number;
  duration?: number;
  hospitalization?: number;
  hasLife?: boolean;
}

export interface ICalculatorVBaseRequest extends ICalculatorBaseRequest {
  capitalDeath?: number;
  capitalIptp?: number;
  capitalIap?: number;
  regime?: string;
  sport?: number;
  motor?: number;
  scaffolding?: number;
  motorcycle?: number;
  risks?: number;
}

export type ICalculatorVAllianzRequest = ICalculatorVBaseRequest;
export type ICalculatorVStaLuciaRequest = ICalculatorVBaseRequest;
export type ICalculatorVSurneRequest = ICalculatorVBaseRequest;
export type ICalculatorVSurneLifeProRequest = ICalculatorVBaseRequest;
export type ICalculatorVHelvetiaRequest = ICalculatorVBaseRequest;
export type ICalculatorVPrebalRequest = ICalculatorVBaseRequest;
export type ICalculatorVZurichRequest = ICalculatorVBaseRequest;

export interface ICalculatorVAxaRequest extends ICalculatorVBaseRequest {
  capitalSevereIllness?: number;
}

export interface ICalculatorVPrebalInvProRequest extends ICalculatorBaseRequest {
  capitalIptp?: number;
  capitalIap?: number;
}

export interface ICalculatorVEStaLuciaRequest extends ICalculatorBaseRequest {
  capitalDeath?: number;
  capitalIptp?: number;
  capitalIap?: number;
}

export interface ICalculatorDepAxaRequest extends ICalculatorBaseRequest {
  severeDependency?: number;
  greatDependency?: number;
}

export interface ICalculatorDepSurneRequest extends ICalculatorBaseRequest {
  capitalIncome?: number;
  severeDependency?: number;
  greatDependency?: number;
}

export interface ICalculatorDecAuraRequest extends ICalculatorBaseRequest {
  chargeable?: boolean;
  taxAdvice?: number;
  burial?: number;
}

export type ICalculatorVAllianzResult = IResponseOutputDto;
export type ICalculatorVAxaResult = IResponseOutputDto;
export type ICalculatorVStaLuciaResult = IResponseOutputDto;
export type ICalculatorVSurneResult = IResponseOutputDto;
export type ICalculatorVSurneLifeProResult = IResponseOutputDto;
export type ICalculatorVHelvetiaResult = IResponseOutputDto;
export type ICalculatorVPrebalResult = IResponseOutputDto;
export type ICalculatorVPrebalInvProResult = IResponseOutputDto;
export type ICalculatorVZurichResult = IResponseOutputDto;
export type ICalculatorAAxaResult = IResponseOutputDto;
export type ICalculatorASurneResult = IResponseOutputDto;
export type ICalculatorAPrebalResult = IResponseOutputDto;
export type ICalculatorITPmEliteResult = IResponseOutputDto;
export type ICalculatorITPmCoegResult = IResponseOutputDto;
export type ICalculatorITSurnePlusResult = IResponseOutputDto;
export type ICalculatorITSurneVipResult = IResponseOutputDto;
export type ICalculatorITPrebalResult = IResponseOutputDto;
export type ICalculatorDepAxaResult = IResponseOutputDto;
export type ICalculatorDepSurneResult = IResponseOutputDto;
export type ICalculatorVEStaLuciaResult = IResponseOutputDto;
export type ICalculatorDecAuraResult = IResponseOutputDto;

export interface IClient {
  clientId: number;
  client: string;
  addressPostalCode: string;
  address: string;
  birthDate: Date;
  actuarialAge: number;
  officeCode: string;
  officeName: string;
  officeAlias: string;
  professionCode: number;
  profession: string;
  statusId: number;
  clientSex: number;
  clientActive: boolean;
  canSeeTrace: boolean;
  lifeLevel: number;
  married: boolean;
  children: number;
  mutual: number;
  retaQuote: number;
  generalRegimeQuote: number;
  deadlineIPT: number;
  canSeePmTICoeg?: boolean;
}

export interface IProjectBasicDto {
  officeName?: string;

  regime: string;
  professionCode: number;
  professionName: string;
  clientId: number;

  effectiveDate: string;
  birthDate: string;
  paymentType: number;
  projectName: string;
  clientName: string;

  clientSex: number;

  sport: number;
  risks: number;
  motorcycle: number;
  motor: number;
  scaffolding: number;
  lifeLevel: number;
  married: boolean;
  children: number;
  saneIntranet: number;
  chargeable: boolean;

  officeAlias: string;
  totalBonus: number;

  life: number;
  accidents: number;
  it: number;
  dependency: number;
  wholeLife: number;
  deaths: number;

  mutual: number;
  retaQuote: number;
  generalRegimeQuote: number;
  iptp: number;
  iap: number;

  lifeRegimeLevel: string;
  temporalState: TemporalState;

  errorMessage?: string;
}

export interface IProjectOutputDto extends IProjectBasicDto {
  id: number;
  age: number;
  province: string;
  postalCode: string;
  clientFullName: string;
  canSeeTrace: boolean;
  canSeePmTICoeg?: boolean;

  allianzLifeOutput: ICalculatorVAllianzRequest;
  axaLifeOutput: ICalculatorVAxaRequest;
  staLuciaLifeOutput: ICalculatorVStaLuciaRequest;
  surneLifeOutput: ICalculatorVSurneRequest;
  surneLifeProOutput: ICalculatorVSurneLifeProRequest;
  prebalLifeOutput: ICalculatorVPrebalRequest;
  helvetiaLifeOutput: ICalculatorVHelvetiaRequest;
  prebalLifeDisabilityOutput: ICalculatorVPrebalInvProRequest;
  zurichLifeOutput: ICalculatorVZurichRequest;
  staLuciaWholeLifeOutput: ICalculatorVEStaLuciaRequest;

  axaAccidentOutput: ICalculatorAAxaRequest;
  surneAccidentOutput: ICalculatorASurneRequest;
  prebalAccidentOutput: ICalculatorAPrebalRequest;

  pmTIEliteOutput: ICalculatorITPmEliteRequest;
  pmTICoegOutput: ICalculatorITPmCoegRequest;
  surneTIOutput: ICalculatorITSurnePlusRequest;
  surneTIVipOutput: ICalculatorITSurneVipRequest;
  prebalTIOutput: ICalculatorITPrebalRequest;

  axaDependencyOutput: ICalculatorDepAxaRequest;
  surneDependencyOutput: ICalculatorDepSurneRequest;

  auraDeathOutput: ICalculatorDecAuraRequest;
}

export interface IGetProjectByIdDto extends IProjectBasicDto {
  id: number;
  age: number;
  province: string;
  postalCode: string;
  clientFullName: string;
  canSeeTrace: boolean;

  allianzLifeOutput: ICalculatorVAllianzRequest;
  axaLifeOutput: ICalculatorVAxaRequest;
  surneLifeOutput: ICalculatorVSurneRequest;
  staLuciaLifeOutput: ICalculatorVStaLuciaRequest;
  surneLifeProOutput: ICalculatorVSurneLifeProRequest;
  prebalLifeOutput: ICalculatorVPrebalRequest;
  helvetiaLifeOutput: ICalculatorVHelvetiaRequest;
  prebalLifeDisabilityOutput: ICalculatorVPrebalInvProRequest;
  zurichLifeOutput: ICalculatorVZurichRequest;

  axaAccidentOutput: ICalculatorAAxaRequest;
  surneAccidentOutput: ICalculatorASurneRequest;
  prebalAccidentOutput: ICalculatorAPrebalRequest;

  pmTIEliteOutput: ICalculatorITPmEliteRequest;
  pmTICoegOutput: ICalculatorITPmCoegRequest;
  surneTIOutput: ICalculatorITSurnePlusRequest;
  surneTIVipOutput: ICalculatorITSurneVipRequest;
  prebalTIOutput: ICalculatorITPrebalRequest;

  axaDependencyOutput: ICalculatorDepAxaRequest;
  surneDependencyOutput: ICalculatorDepSurneRequest;

  auraDeathOutput: ICalculatorDecAuraRequest;
}

export interface IGetProjectResponse {
  succeeded: boolean;
  result: IProjectOutputDto;
}

export interface IGetProjectByIdResponse {
  succeeded: boolean;
  result: IGetProjectByIdDto;
}

export interface IRawProjectDto {
  projectId: number;
  projectName: string;
  projectDate: string | null;
  totalBonus: number;
  clientId: string;
  clientName?: string;
  professionId?: number;
  professionName?: string;
  clientAge: number | null;
  officeAlias: string;
  officeName: string;
  user: string;
}

export interface IGetProjectsOutputDto {
  projects: IRawProjectDto[];
  itemCount: number;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface IApiResponse<T> {
  succeeded: boolean;
  result: T;
}

export interface IProjectInputDto extends IProjectBasicDto {
  allianzLifeInput: ICalculatorVAllianzRequest;
  axaLifeInput: ICalculatorVAxaRequest;
  staLuciaLifeInput: ICalculatorVStaLuciaRequest;
  surneLifeInput: ICalculatorVSurneRequest;
  surneLifeProInput: ICalculatorVSurneLifeProRequest;
  helvetiaLifeInput: ICalculatorVHelvetiaRequest;
  prebalLifeInput: ICalculatorVPrebalRequest;
  prebalLifeDisabilityInput: ICalculatorVPrebalInvProRequest;
  zurichLifeInput: ICalculatorVZurichRequest;
  wholeLifeStaLuciaInput: ICalculatorVEStaLuciaRequest;

  axaAccidentInput: ICalculatorAAxaRequest;
  surneAccidentInput: ICalculatorASurneRequest;
  prebalAccidentInput: ICalculatorAPrebalRequest;

  pmTIEliteInput: ICalculatorITPmEliteRequest;
  pmTICoegInput: ICalculatorITPmCoegRequest;
  prebalTIInput: ICalculatorITPrebalRequest;
  surneTIInput: ICalculatorITSurnePlusRequest;
  surneTIVipInput: ICalculatorITSurneVipRequest;

  axaDependencyInput: ICalculatorDepAxaRequest;
  surneDependencyInput: ICalculatorDepSurneRequest;

  auraDeathInput: ICalculatorDecAuraRequest;
}

export interface IDropDownMenusOutputDto<T> {
  value: T;
  text: string;
}

export interface ISearchRequest extends IBasePaginatedFilter {
  loggedUserId: string;
  projectDateFrom?: any;
  projectDateTo?: any;
  projectName?: string;
  clientDescription?: string;
  officeDescription?: string;
  userDescription?: string;
}

export interface ISearchResult {
  projectId?: number;
  projectDate?: Date;
  projectName?: string;
  totalBonus?: number;
  clientId?: number;
  clientName?: string;
  clientProfession?: string;
  clientAge?: string;
  userId?: string;
  userName?: string;
  officeAlias?: string;
  officeName?: string;
}
